import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import type {
    InboundConnectorCreateRequest,
    InboundConnectorUpdateRequest,
    RestInboundConfigUpdateRequest,
    ScimInboundConfigUpdateRequest,
    InboundConnectorSettingsUpdateRequest,
    InboundAttributeMapping,
    ScriptInboundConfigCreateRequest,
    ScriptInboundConfigUpdateRequest,
} from '@/types/inbound-connector'
import {useToast} from '@/hooks/use-toast'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'


const QUERY_KEY = 'inbound-connectors'
const SYNC_RUNS_KEY = 'inbound-connectors-sync-runs'
const SYNC_STATUS_KEY = 'inbound-connectors-sync-status'
const REST_CONFIG_KEY = 'inbound-connectors-rest-config'
const SCIM_CONFIG_KEY = 'inbound-connectors-scim-config'
const ATTR_MAPPINGS_KEY = 'inbound-connectors-attribute-mappings'
const SCRIPT_CONFIG_KEY = 'inbound-connectors-script-config'


export function useInboundConnectors(queryParams: string, options: {enabled?: boolean} = {}) {
    const services = useServices()
    return useQuery({
        queryKey: [QUERY_KEY, queryParams],
        queryFn: () => services.inboundConnectors.list(Object.fromEntries(new URLSearchParams(queryParams))),
        enabled: options.enabled ?? true,
    })
}

export function useInboundConnector(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [QUERY_KEY, id],
        queryFn: () => services.inboundConnectors.detail(id!),
        enabled: !!id,
    })
}

export function useCreateInboundConnector() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: InboundConnectorCreateRequest) =>
            services.inboundConnectors.create(data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.createSuccess})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useUpdateInboundConnector(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: InboundConnectorUpdateRequest) =>
            services.inboundConnectors.update(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.updateSuccess})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, id]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useDeleteInboundConnector() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (id: string) =>
            services.inboundConnectors.remove(id),
        onSuccess: (_data, id) => {
            toast({description: i18n.inboundConnectors.deleteSuccess})
            // WHY: drop the soon-to-404 detail entry from cache before the
            // detail page unmounts — otherwise a background refetch fires
            // against the deleted id and the global mutationCache surfaces a
            // GN0002 banner that flashes during the navigation transition.
            queryClient.removeQueries({queryKey: [QUERY_KEY, id]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useEnableConnector(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: () =>
            services.inboundConnectors.enable(id),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.enableSuccess})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, id]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useDisableConnector(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: () =>
            services.inboundConnectors.disable(id),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.disableSuccess})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, id]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
        },
    })
}

export function useTriggerSync(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (_syncType: 'full') => services.inboundConnectors.syncFull(id),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.syncStarted})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, id]})
            queryClient.invalidateQueries({queryKey: [SYNC_STATUS_KEY, id]})
            queryClient.invalidateQueries({queryKey: [SYNC_RUNS_KEY, id]})
        },
    })
}

export function useSyncStatus(id: string | undefined, polling: boolean) {
    const services = useServices()
    return useQuery({
        queryKey: [SYNC_STATUS_KEY, id],
        queryFn: () => services.inboundConnectors.syncStatus(id!),
        enabled: !!id,
        refetchInterval: polling ? 3000 : false,
    })
}

export function useTestConnection(id: string) {
    const services = useServices()
    return useMutation({
        mutationFn: () =>
            services.inboundConnectors.test(id),
    })
}

export function useRestConfig(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [REST_CONFIG_KEY, id],
        queryFn: () => services.inboundConnectors.restConfig(id!),
        enabled: !!id,
        retry: false,
    })
}

export function useScimConfig(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [SCIM_CONFIG_KEY, id],
        queryFn: () => services.inboundConnectors.scimConfig(id!),
        enabled: !!id,
        retry: false,
    })
}

export function useUpdateRestConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: RestInboundConfigUpdateRequest) =>
            services.inboundConnectors.updateRestConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.restConfigSaveSuccess})
            queryClient.invalidateQueries({queryKey: [REST_CONFIG_KEY, id]})
        },
    })
}

export function useUpdateScimConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: ScimInboundConfigUpdateRequest) =>
            services.inboundConnectors.updateScimConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.scimConfigSaveSuccess})
            queryClient.invalidateQueries({queryKey: [SCIM_CONFIG_KEY, id]})
        },
    })
}

export function useUpdateConnectorSettings(id: string, options: { silent?: boolean } = {}) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: InboundConnectorSettingsUpdateRequest) =>
            services.inboundConnectors.updateSettings(id, data),
        onSuccess: () => {
            if (!options.silent) {
                toast({description: i18n.inboundConnectors.settingsSaveSuccess})
            }
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, id]})
        },
    })
}

export function useUpdateInboundAttributeMappings(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: InboundAttributeMapping[] | null) =>
            services.inboundConnectors.updateAttributeMappings(id, {attributeMappings: data}),
        onSuccess: (_, variables) => {
            toast({
                description: variables !== null && variables.length > 0
                    ? i18n.inboundAttributeMappings.saved
                    : i18n.inboundAttributeMappings.cleared,
            })
            queryClient.invalidateQueries({queryKey: [ATTR_MAPPINGS_KEY, id]})
            queryClient.invalidateQueries({queryKey: [REST_CONFIG_KEY, id]})
            queryClient.invalidateQueries({queryKey: [SCRIPT_CONFIG_KEY, id]})
        },
    })
}

export function useScriptInboundConfig(connectorId: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [SCRIPT_CONFIG_KEY, connectorId],
        queryFn: () => services.inboundConnectors.scriptConfig(connectorId!),
        enabled: !!connectorId,
        retry: false,
    })
}

export function useCreateScriptInboundConfig(connectorId: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: ScriptInboundConfigCreateRequest) =>
            services.inboundConnectors.createScriptConfig(connectorId, data),
        onSuccess: () => {
            toast({description: i18n.scriptConnector.createSuccess})
            queryClient.invalidateQueries({queryKey: [SCRIPT_CONFIG_KEY, connectorId]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, connectorId]})
        },
    })
}

export function useUpdateScriptInboundConfig(connectorId: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    return useMutation({
        mutationFn: (data: ScriptInboundConfigUpdateRequest) =>
            services.inboundConnectors.updateScriptConfig(connectorId, data),
        onSuccess: () => {
            toast({description: i18n.scriptConnector.updateSuccess})
            queryClient.invalidateQueries({queryKey: [SCRIPT_CONFIG_KEY, connectorId]})
            queryClient.invalidateQueries({queryKey: [QUERY_KEY, connectorId]})
        },
    })
}

export function useValidateInboundScript(connectorId: string) {
    const services = useServices()
    return useMutation({
        mutationFn: (script: string) =>
            services.inboundConnectors.validateInboundScript(connectorId, {script}),
    })
}

export function useTestInboundScriptConnection(connectorId: string) {
    const services = useServices()
    return useMutation({
        mutationFn: () =>
            services.inboundConnectors.testInboundScriptConnection(connectorId),
    })
}
